import { type FC, type ReactNode } from 'react';
import classnames from 'classnames';

import { getSrcsetString, getSrcsetUrl } from 'dibs-image-utils/exports/srcSet.ts';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { Button } from 'dibs-elements/exports/Button.tsx';

import { trackTradeBenefitsEvent } from './tradeBenefitsTracking.ts';

import sharedStyles from './TradeBenefitsSharedStyles.scss';
import styles from './TradeBenefitsTile.scss';
import dibsCss from 'dibs-css';

const IMAGE_WIDTHS = [400, 600, 800, 1200];
const IMAGE_SIZES = '(max-width: 600px) 100vw, 33vw';

const TradeBenefitsTile: FC<{
    title: ReactNode;
    description: ReactNode;
    imageUrl: string;
    ctaCopy: ReactNode;
    ctaUrl: string;
    trigger: string;
    isTrade: boolean;
}> = ({ title, description, imageUrl, ctaCopy, ctaUrl, trigger, isTrade }) => {
    const isInPageAnchor = ctaUrl.startsWith('#');

    return (
        <div
            className={classnames(
                styles.container,
                dibsCss.relative,
                dibsCss.roundedXsmall,
                dibsCss.textCenter,
                dibsCss.overflowHidden
            )}
        >
            <img
                src={getSrcsetUrl(imageUrl, 800)}
                srcSet={getSrcsetString(imageUrl, IMAGE_WIDTHS)}
                sizes={IMAGE_SIZES}
                alt=""
                loading="lazy"
                decoding="async"
                className={classnames(
                    dibsCss.absolute,
                    dibsCss.left0,
                    dibsCss.top0,
                    dibsCss.hFull,
                    dibsCss.wFull,
                    dibsCss.objectCover
                )}
            />
            <div
                className={classnames(
                    dibsCss.relative,
                    dibsCss.hFull,
                    dibsCss.flex,
                    dibsCss.flexCol,
                    dibsCss.itemsCenter,
                    dibsCss.justifyBetween
                )}
            >
                <div>
                    <HeadingLevel>
                        {Heading => (
                            <Heading
                                className={classnames(
                                    sharedStyles.headingMedium,
                                    styles.heading,
                                    dibsCss.mt0,
                                    dibsCss.mbXsmall
                                )}
                            >
                                {title}
                            </Heading>
                        )}
                    </HeadingLevel>
                    <p
                        className={classnames(
                            styles.description,
                            dibsCss.sassyFontBody,
                            dibsCss.m0
                        )}
                    >
                        {description}
                    </p>
                </div>
                {isTrade && (
                    <Button
                        href={ctaUrl}
                        target={isInPageAnchor ? undefined : '_blank'}
                        onClick={() =>
                            trackTradeBenefitsEvent({
                                stepInteractionName: 'benefit cta clicked',
                                trigger,
                            })
                        }
                        style="circle"
                        customColors={{
                            background: 'sassyColorWhite',
                            text: 'sassyColorNoir',
                            hoverBackground: 'sassyColorAlabaster',
                            hoverText: 'sassyColorNoir',
                        }}
                        fullWidth
                        className={styles.button}
                    >
                        {ctaCopy}
                    </Button>
                )}
            </div>
        </div>
    );
};

export { TradeBenefitsTile };
